import React, { useState, useEffect, useMemo } from "react";
import { Tag, Space, Button, Select, Modal, TimePicker, Typography, Checkbox,
         Popconfirm, Spin, Tooltip, message } from "antd";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import GxTable from "../../components/GxTable";
import { getCardex, manualCorrectDay, setAttendanceReview,
         bulkApproveAttendance } from "../../api/client";
import { errText, runWithAdminPin } from "../../utils/lockedMonth";
import { stMeta, MISSING_HINT, REVIEW_DECISIONS, rowClass,
         fa, hhmm, DAY_FILTERS, minutesLabel } from "./attendanceMeta";

const { Text } = Typography;

/**
 * کاردکسِ ماهِ یک نفر — بالای لیستِ نفرات (نه داخلِ ردیفش) باز می‌شود (v2.108.0).
 *
 * خواستهٔ کاربر (۱۴۰۵/۰۷/۰۱): «لیست پرسنل جداگانه پایین باشد… وقتی روی هر اسم
 * می‌زنیم بالاش جدولِ هفتگی‌هاش بیاد… سه تا تیک: فقط غیبت‌ها، خطاهای تردد (کمتر یا
 * بیشتر از دو تردد) که باید اصلاح بشن، و … کسرکار به دقیقه و ساعت… وضعیت بغلِ تاریخ،
 * منوها جابه‌جا بشن».
 *
 * یک جدول برای کلِ ماه (نه یک جدول per هفته) تا جابه‌جاییِ ستون‌ها همه‌جا یکی باشد؛
 * مرزِ هفته‌ها با خطِ پررنگ و ستونِ «هفته»، و جمعِ هر هفته در نوارِ بالا.
 */
export default function PersonDaysPanel({
  employeeId, employeeName, year, month, baseHours = 7,
  locked = false, onChanged,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);  // کلیدهای DAY_FILTERS
  const [edit, setEdit] = useState(null);      // روزِ در حالِ اصلاح
  const [inT, setInT] = useState(null);
  const [outT, setOutT] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const r = await getCardex(employeeId, year, month);
      setData(r.data);
    } catch { message.error("خطا در بارگذاری کاردکس"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId, year, month]);

  /* روزها با شمارهٔ هفته — هفته از شنبه شروع می‌شود (همان گروه‌بندیِ بک‌اند) */
  const allDays = useMemo(() => (data?.weeks || []).flatMap(
    (w, i) => w.map((d, j) => ({ ...d, week: i + 1, week_start: j === 0 && i > 0 }))), [data]);

  /* جمع‌ها همیشه از **کلِ ماه**، نه روزهای فیلترشده — وگرنه فیلتر شبیهِ «کم‌شدنِ
     کارکرد» به‌نظر می‌رسد و حسابدار به عدد بی‌اعتماد می‌شود. */
  const totals = useMemo(() => {
    const sum = (f) => allDays.reduce((s, d) => s + (Number(f(d)) || 0), 0);
    const shortage = sum((d) => d.shortage_minutes);
    return {
      worked: sum((d) => d.actual_work_minutes),
      shortage,
      forgiven: sum((d) => d.ignored_shortage_minutes),
      late: sum((d) => d.late_minutes),
      early: sum((d) => d.early_exit_minutes),
      overtime: sum((d) => d.overtime_minutes),
      shortage_days: baseHours > 0 ? shortage / (baseHours * 60) : 0,
      paid_days: allDays.filter(
        (d) => !["absent", "holiday_penalty", "before_hire", "future", "after_leave"].includes(d.status)).length,
      absent_days: allDays.reduce((s, d) => s + (d.absence_deduction_day_count || 0), 0),
      pending: data?.summary?.pending ?? 0,
    };
  }, [allDays, baseHours, data]);

  const weekTotals = useMemo(() => {
    const m = new Map();
    allDays.forEach((d) => {
      const w = m.get(d.week) || { worked: 0, shortage: 0 };
      w.worked += d.actual_work_minutes || 0;
      w.shortage += d.shortage_minutes || 0;
      m.set(d.week, w);
    });
    return [...m.entries()];
  }, [allDays]);

  const matches = (d) => !filters.length
    || filters.some((k) => DAY_FILTERS.find((f) => f.key === k)?.test(d));
  const visible = allDays.filter(matches);
  const countOf = (f) => allDays.filter(f.test).length;

  // ---- اصلاحِ ورود/خروج — انتخاب از ترددهای خام یا تایپِ ساعتِ جاافتاده ----
  const openEdit = (row) => {
    setInT(row.entry_time ? dayjs(row.entry_time, "HH:mm") : null);
    setOutT(row.exit_time ? dayjs(row.exit_time, "HH:mm") : null);
    setEdit(row);
  };
  const saveEdit = async () => {
    if (inT && outT && !outT.isAfter(inT)) return message.warning("خروج باید بعد از ورود باشد");
    setSaving(true);
    try {
      const body = {
        employee_id: employeeId, work_date: edit.date,
        check_in: inT ? inT.format("HH:mm:00") : null,
        check_out: outT ? outT.format("HH:mm:00") : null,
      };
      // ماهِ نهایی‌شده ⇒ کدِ مدیریتی یا درخواست برای مدیر (v2.124.0)
      const out = await runWithAdminPin((pin) => manualCorrectDay(body, pin), { request: body });
      if (out?.requested) { setEdit(null); }
      else if (out) { message.success("اصلاح ثبت شد"); setEdit(null); await load(); onChanged?.(); }
    } catch (e) { message.error(errText(e, "خطا در اصلاح")); }
    setSaving(false);
  };
  const decide = async (row, status) => {
    try {
      const out = await runWithAdminPin((pin) => setAttendanceReview({ employee_id: employeeId, work_date: row.date,
        review_status: status, review_reason: row.status }, pin));
      if (out) { message.success("تصمیم ثبت شد"); await load(); onChanged?.(); }
    } catch (e) { message.error(errText(e, "خطا در ثبت تصمیم")); }
  };
  const approveAll = async () => {
    try {
      const r = await runWithAdminPin((pin) => bulkApproveAttendance({ employee_id: employeeId, year, month }, pin));
      if (r) { message.success(r.data?.message || "تأیید شد"); await load(); onChanged?.(); }
    } catch (e) { message.error(errText(e, "خطا در تأییدِ گروهی")); }
  };

  const noDay = (r) => ["before_hire", "future", "after_leave"].includes(r.status);
  const columns = [
    { title: "تاریخ", dataIndex: "jalali_date", key: "date", width: 96,
      render: (v) => <span className="gx-time">{v}</span> },
    /* وضعیت کنارِ تاریخ — اولین چیزی که حسابدار دنبالش است */
    { title: "وضعیت", dataIndex: "status", key: "status", width: 150,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Space size={4} wrap>
            <Tag color={stMeta(v).color} style={{ margin: 0 }}>{stMeta(v).label}</Tag>
            {r.is_manual && <Tag color="green" style={{ margin: 0 }}>اصلاح‌شده</Tag>}
            {r.reviewed && !r.is_manual && <Tag color="blue" style={{ margin: 0 }}>بازبینی‌شده</Tag>}
          </Space>
          {r.holiday_name && <Text type="secondary" style={{ fontSize: 10 }}>{r.holiday_name}</Text>}
          {r.day_rule && (
            <Tooltip title={r.day_rule.is_noop
              ? `اصلاحِ همگانی ثبت شده ولی اثری ندارد — همان ساعتِ عادیِ ${r.day_rule.normal_start}–${r.day_rule.normal_end}.`
              : `ساعتِ عادی ${r.day_rule.normal_start}–${r.day_rule.normal_end} بود؛ این روز ${r.day_rule.label}.`}>
              <Tag color={r.day_rule.is_noop ? "default" : "cyan"} style={{ fontSize: 10, margin: 0 }}>
                اصلاحِ همگانی{r.day_rule.note ? `: ${r.day_rule.note}` : ""}
              </Tag>
            </Tooltip>
          )}
          {r.status === "incomplete" && r.likely_missing && (
            <span style={{ fontSize: 10, color: "#d46b08" }}>{MISSING_HINT[r.likely_missing]}</span>
          )}
        </Space>
      ) },
    { title: "روز", dataIndex: "weekday", key: "weekday", width: 72 },
    { title: "هفته", dataIndex: "week", key: "week", width: 56, align: "center", render: (v) => fa(v) },
    /* v2.152.8: تک‌پانچ در روزِ اصلاحِ همگانی ⇒ سمتِ جاافتاده از مرزِ پنجرهٔ آن روز پر می‌شود (کم‌رنگ + توضیح) */
    { title: "ورود", dataIndex: "entry_time", key: "in", width: 62,
      render: (v, r) => (!v ? "—" : r.auto_filled_by_day_rule === "entry"
        ? <Tooltip title="ورود ثبت نشده بود — طبقِ اصلاحِ همگانیِ این روز، شروعِ پنجره گذاشته شد">
            <span className="gx-time" style={{ color: "#08979c", fontStyle: "italic" }}>{v}*</span></Tooltip>
        : <span className="gx-time">{v}</span>) },
    { title: "خروج", dataIndex: "exit_time", key: "out", width: 62,
      render: (v, r) => (!v ? "—" : r.auto_filled_by_day_rule === "exit"
        ? <Tooltip title="خروج ثبت نشده بود — طبقِ اصلاحِ همگانیِ این روز، پایانِ پنجره گذاشته شد">
            <span className="gx-time" style={{ color: "#08979c", fontStyle: "italic" }}>{v}*</span></Tooltip>
        : <span className="gx-time">{v}</span>) },
    /* ترددهای خامِ دستگاه — تعدادِ غیرِ ۲ یعنی خطای تردد که باید اصلاح شود */
    { title: "ترددها", dataIndex: "raw_punches", key: "punches", width: 150,
      render: (v, r) => {
        const n = r.punch_count || 0;
        if (noDay(r) || (!n && r.status === "holiday")) return "—";
        const paired = (r.punch_pairs || []).length > 0;
        const bad = n !== 2 && !r.is_manual && !paired;
        return (
          <Space size={2} wrap>
            <Tag color={bad ? "orange" : paired ? "blue" : undefined} style={{ margin: 0 }}>{fa(n)}</Tag>
            {/* v2.152.10: رفت‌وآمدِ وسطِ روز ⇒ جفت‌جفت (ورود–خروج) و زمانِ بیرون‌رفتن */}
            {paired
              ? r.punch_pairs.map(([a, b], i) => (
                <span key={i} className="gx-time" style={{ fontSize: 11 }}>{a}–{b}</span>))
              : (v || []).map((t, i) => (
                <span key={i} className="gx-time" style={{ fontSize: 11 }}>{t}</span>))}
            {paired && r.away_minutes > 0 && (
              <Tooltip title="بیرون‌رفتنِ وسطِ روز — جزوِ کسرکار">
                <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>بیرون {hhmm(r.away_minutes)}</Tag>
              </Tooltip>)}
          </Space>
        );
      } },
    { title: "کارکرد", dataIndex: "actual_work_minutes", key: "worked", width: 70, align: "center",
      render: (v) => v ? hhmm(v) : "—" },
    /* کسری به دقیقه + ساعت؛ کسریِ داخلِ تلورانس «(بخشیده)» ولی حساب نمی‌شود */
    { title: "کسرکار", dataIndex: "shortage_minutes", key: "shortage", width: 120,
      render: (v, r) => v > 0
        ? <span style={{ color: "#cf1322" }}>{minutesLabel(v)}</span>
        : (r.ignored_shortage_minutes > 0
            ? <Tooltip title="داخلِ تلورانسِ روزانه — در کسرکار و حقوق حساب نمی‌شود">
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {fa(r.ignored_shortage_minutes)} دقیقه (بخشیده)</Text>
              </Tooltip>
            : "—") },
    { title: "اضافه‌کاری", dataIndex: "overtime_minutes", key: "overtime", width: 110,
      render: (v) => v > 0 ? <Tag color="green" style={{ margin: 0 }}>{minutesLabel(v)}</Tag> : "—" },
    { title: "تصمیم", key: "decision", width: 170,
      render: (_, r) => (noDay(r) ? "—" : (
        <Select size="small" style={{ width: 160 }} placeholder="تصمیم…" allowClear={false}
          value={r.review_status || undefined} disabled={locked}
          onChange={(v) => decide(r, v)}
          options={REVIEW_DECISIONS.map((d) => ({ value: d.v, label: d.l }))} />
      )) },
    { title: "اصلاح", key: "edit", width: 90,
      render: (_, r) => (noDay(r) ? null : (
        <Button size="small" icon={<EditOutlined />} disabled={locked}
          type={r.status === "incomplete" || r.status === "multi_punch" ? "primary" : "default"}
          onClick={() => openEdit(r)}>ورود/خروج</Button>
      )) },
  ];

  if (loading && !data) return <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>;
  if (!data) return null;

  return (
    <div>
      {/* سه تیکِ خواسته‌شده — هرکدام روزهای همان نوع را نگه می‌دارد (چندتایی = یا) */}
      <Space wrap style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>فقط نمایش:</Text>
        {DAY_FILTERS.map((f) => {
          const n = countOf(f);
          return (
            <Tooltip key={f.key} title={f.help}>
              <Checkbox checked={filters.includes(f.key)} disabled={!n && !filters.includes(f.key)}
                onChange={(e) => setFilters((prev) => (e.target.checked
                  ? [...prev, f.key] : prev.filter((x) => x !== f.key)))}>
                <Tag color={f.color} style={{ margin: 0 }}>{f.label}: {fa(n)}</Tag>
              </Checkbox>
            </Tooltip>
          );
        })}
        {filters.length > 0 && (
          <Button size="small" type="link" onClick={() => setFilters([])}>همهٔ روزها</Button>
        )}
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
        {!locked && totals.pending > 0 && (
          <Popconfirm title={`همهٔ ${fa(totals.pending)} موردِ رسیدگی‌نشدهٔ «${employeeName || ""}» تأیید شود؟`}
            onConfirm={approveAll} okText="بله" cancelText="خیر">
            <Button size="small" type="primary">تأیید همهٔ موارد ({fa(totals.pending)})</Button>
          </Popconfirm>
        )}
        {totals.pending === 0 && <Tag color="green">رسیدگی‌شده</Tag>}
      </Space>

      {/* جمعِ هر هفته — جدول یکی است، هفته‌ها با خطِ پررنگ جدا شده‌اند */}
      <Space wrap size={6} style={{ marginBottom: 8 }}>
        {weekTotals.map(([w, t]) => (
          <Tag key={w} style={{ margin: 0 }}>
            هفتهٔ {fa(w)}: کارکرد {hhmm(t.worked)}
            {t.shortage > 0 && <span style={{ color: "#cf1322" }}> — کسری {fa(t.shortage)} دقیقه</span>}
          </Tag>
        ))}
      </Space>

      <GxTable layoutId="att-person-days" gxToolbar={false} gxSearch={false}
        dataSource={visible} columns={columns} rowKey="date" size="small"
        pagination={false} scroll={{ x: "max-content" }}
        rowClassName={(r) => [rowClass(r), r.week_start ? "att-week-start" : ""].join(" ")}
        locale={{ emptyText: "با این فیلتر روزی نیست" }} />
      {filters.length > 0 && (
        <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
          فیلتر فعال است؛ جمع‌های پایین همچنان مالِ کلِ ماه‌اند.
        </div>
      )}

      {/* جمعِ کلِ ماه — همان اعدادِ لیستِ نفرات */}
      <div style={{ background: "var(--surface-2, #fafafa)", border: "1px solid var(--border, #eee)",
                    borderRadius: 8, padding: "8px 12px", marginTop: 8 }}>
        <Space wrap size={14}>
          <b>جمعِ ماه:</b>
          <span>کارکرد <b>{hhmm(totals.worked)}</b></span>
          <span>دیرکرد {minutesLabel(totals.late)}</span>
          <span>تعجیل {minutesLabel(totals.early)}</span>
          <span>کسرکار <b style={{ color: totals.shortage ? "#cf1322" : undefined }}>
            {minutesLabel(totals.shortage)}</b></span>
          <Tooltip title={`کسرکار ÷ ${fa(baseHours)} ساعت (مبنای روزِ حقوقی در تنظیمات). همین عدد × حقوقِ روزانه از حقوق کم می‌شود؛ یعنی هر ${fa(baseHours * 60)} دقیقه کسرکار = یک روز حقوق.`}>
            <span style={{ borderBottom: "1px dotted #999", cursor: "help" }}>
              معادلِ روزِ کسرکار <b style={{ color: totals.shortage_days ? "#cf1322" : undefined }}>
                {fa(totals.shortage_days.toFixed(2))}</b></span>
          </Tooltip>
          <span>بخشیده {minutesLabel(totals.forgiven)}</span>
          {totals.overtime > 0 && <span>اضافه‌کاری <b>{minutesLabel(totals.overtime)}</b></span>}
          <span>روزِ پرداختی <b>{fa(totals.paid_days)}</b></span>
          <span>غیبت <b style={{ color: totals.absent_days ? "#cf1322" : undefined }}>
            {fa(totals.absent_days)}</b></span>
        </Space>
      </div>

      <Modal open={!!edit} title={`اصلاحِ ورود/خروج — ${edit?.jalali_date || ""} (${edit?.weekday || ""})`}
        onCancel={() => setEdit(null)} onOk={saveEdit} confirmLoading={saving}
        okText="ثبت" cancelText="انصراف" destroyOnClose>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
          ثبتِ دستی بر فایلِ دستگاه مقدم است و با ایمپورتِ دوباره پاک نمی‌شود. خالی‌گذاشتنِ هر دو
          یعنی «غایب».
          {edit?.status === "incomplete" && edit?.likely_missing && (
            <div style={{ color: "#d46b08", marginTop: 4 }}>{MISSING_HINT[edit.likely_missing]} —
              ساعتِ جاافتاده را وارد کنید.</div>
          )}
        </div>
        {edit?.raw_punches?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 12 }}>ترددهای خامِ دستگاه
              {edit.raw_punches.length > 2 ? " — دو تا را به‌عنوانِ ورود و خروج انتخاب کنید؛ بقیه کنار می‌روند:" : ":"}
            </Text>
            <Space direction="vertical" size={4} style={{ marginTop: 6, width: "100%" }}>
              {edit.raw_punches.map((t, i) => {
                const isIn = inT && inT.format("HH:mm") === t;
                const isOut = outT && outT.format("HH:mm") === t;
                return (
                  <Space key={i}>
                    <span className="gx-time" style={{ minWidth: 48, display: "inline-block",
                      textDecoration: !isIn && !isOut && edit.raw_punches.length > 2 ? "line-through" : "none",
                      color: !isIn && !isOut && edit.raw_punches.length > 2 ? "#999" : undefined }}>{t}</span>
                    <Button size="small" type={isIn ? "primary" : "default"}
                      onClick={() => setInT(dayjs(t, "HH:mm"))}>ورود</Button>
                    <Button size="small" type={isOut ? "primary" : "default"}
                      onClick={() => setOutT(dayjs(t, "HH:mm"))}>خروج</Button>
                  </Space>
                );
              })}
            </Space>
          </div>
        )}
        <Space wrap>
          <span>ورود</span>
          <TimePicker value={inT} onChange={setInT} format="HH:mm" minuteStep={1} allowClear />
          <span>خروج</span>
          <TimePicker value={outT} onChange={setOutT} format="HH:mm" minuteStep={1} allowClear />
          {/* v2.152.9: پانچِ اشتباه (اصلاً نباید می‌آمد) ⇒ هر دو خالی = بی‌پانچ (غیبت) */}
          <Button size="small" danger disabled={!inT && !outT}
            onClick={() => { setInT(null); setOutT(null); }}>خالی کردنِ ورود و خروج</Button>
        </Space>
        {!inT && !outT && (
          <div style={{ fontSize: 12, color: "#cf1322", marginTop: 8 }}>
            ورود و خروج خالی است ⇒ با «ثبت» این روز بی‌پانچ (غیبت) حساب می‌شود.
          </div>
        )}
      </Modal>
    </div>
  );
}
